import React, { useState, useEffect, useCallback } from "react";
import clsx from "clsx";
import moment from "moment";
import { Col, Row, Container } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { destroy } from "redux-form";

import { MailToLink } from "components";
import {
	CustomLoginSteps,
	EmailUrls,
	LoginFormName,
	LoginHelpText,
} from "globalConstants";
import { getFirstAvailableSideMenuLink } from "helpers";
import { useClearCache } from "hooks";
import { uiActions, userActions } from "store";

import AuroraSummitBanner from "./banners/auroraSummitBanner";
import BannerManager from "./banners/bannerManager";
import MfaBanner from "./banners/mfaBanner";
import EmailVerificationForm from "./emailVerificationForm";
import { EmailWelcome } from "./emailWelcome";
import { LoginWelcome } from "./loginWelcome";
import PasswordForm from "./passwordForm";
import UsernameForm from "./usernameForm";

import { ReactComponent as Logo } from "static/images/logo.svg";
import { ReactComponent as CrmLogo } from "static/images/riseCrm.svg";
import styles from "./login.module.scss";

const LoginPage = () => {
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const redirectTo = location?.state?.from?.pathname || null;

	const { UsernameStep, PasswordStep, EmailStep } = CustomLoginSteps;

	const { checkUpdates } = useClearCache();
	const [step, setStep] = useState(UsernameStep);
	const next = () => setStep(i => i + 1);

	const [isEmailUnique, setIsEmailUnique] = useState(true);
	const [domains, setDomains] = useState([]);
	const [attempts, setAttempts] = useState(0);

	useEffect(() => {
		dispatch(uiActions.hideSessionExpirationModal());
		return () => dispatch(destroy(LoginFormName));
	}, [dispatch]);

	const handleLogin = useCallback(() => {
		checkUpdates({ autoReload: true });
		redirectTo && history.push(redirectTo);
		dispatch(uiActions.showSpinnerOverlay(false));
		dispatch(userActions.getPermissions()).then(() => {
			history.push(
				redirectTo || getFirstAvailableSideMenuLink()?.to || "/",
			);
		});
	}, [checkUpdates, dispatch, history, redirectTo]);

	const isEmailStep = step === EmailStep;
	const isUsernameStep = step === UsernameStep;
	const isPasswordStep = step === PasswordStep;

	const now = moment().format();
	const auroraSummitExpirationDate = "2024-09-17T00:00:00";
	const isAuroraSummitBanner = now < auroraSummitExpirationDate;

	return (
		<div className={styles.loginPage}>
			{step !== EmailStep && (
				<BannerManager
					body={
						isAuroraSummitBanner ? (
							<AuroraSummitBanner />
						) : (
							<MfaBanner />
						)
					}
					name={isAuroraSummitBanner ? "auroraSummit" : "mfa"}
					expirationDate={
						isAuroraSummitBanner ? auroraSummitExpirationDate : null
					}
				/>
			)}
			<Container className={styles.loginContainer}>
				<Row className={styles.login}>
					<Col
						className={clsx(styles.loginBlock, "align-self-center")}
						md={{ span: 8, offset: 2 }}
					>
						<Col md={{ span: 8, offset: 2 }}>
							{!isEmailStep && (
								<div className={styles.logosHolder}>
									<Logo className={styles.mainLogo} />
									<CrmLogo className={styles.secondaryLogo} />
								</div>
							)}

							<div className={styles.welcomBlock}>
								{isEmailStep ? (
									<EmailWelcome domains={domains} />
								) : (
									<LoginWelcome />
								)}
							</div>
							<div className={styles.loginForm}>
								{isUsernameStep && (
									<UsernameForm
										next={next}
										setIsEmailUnique={setIsEmailUnique}
										setDomains={setDomains}
										setAttempts={setAttempts}
									/>
								)}
								{isPasswordStep && (
									<PasswordForm
										next={next}
										handleLogin={handleLogin}
										isEmailUnique={isEmailUnique}
									/>
								)}

								{isEmailStep && (
									<EmailVerificationForm
										handleLogin={handleLogin}
										setStep={setStep}
										attempts={attempts}
										domains={domains}
									/>
								)}

								<div className="col-md-12 text-center hidden">
									<MailToLink
										link={EmailUrls.toHelp}
										text={LoginHelpText}
									/>
								</div>
							</div>
						</Col>
					</Col>
				</Row>
			</Container>
		</div>
	);
};

export { LoginPage };
